import axios from 'axios';
import type { DateTime } from 'luxon';
import { getFingerprint } from '../utils/fingerprint';

export default class AuthService
{
    public static async getDeviceInfo(): Promise<DeviceInfo>
    {
        const fingerprint = await getFingerprint();

        return {
            deviceId: fingerprint.hash,
            deviceName: fingerprint.components.userAgent
        };
    }

    public static async login(username: string, password: string, captchaResult: string, rememberMe: boolean = false, forceLogin: boolean = false)
    {
        const device = await AuthService.getDeviceInfo();

        return (await axios.post<AuthTokenModel>('auth/login', {
            username: username,
            password: password,
            captchaResult: captchaResult,
            deviceId: device.deviceId,
            deviceName: device.deviceName,
            rememberMe: rememberMe,
            forceLogin: forceLogin
        }, {
            withCredentials: true
        })).data;
    }

    public static async verifyMfa(password: string, code: string, token: string)
    {
        const device = await AuthService.getDeviceInfo();

        return (await axios.post<TokenModel>('auth/login/verify-mfa', {
            password,
            code,
            token,
            deviceId: device.deviceId,
            deviceName: device.deviceName
        }, {
            withCredentials: true
        })).data;
    }

    public static async logout()
    {
        const device = await AuthService.getDeviceInfo();

        return (await axios.get<Statement>('auth/logout', {
            params: {
                id: device.deviceId,
                name: device.deviceName
            },
            withCredentials: true
        })).data;
    }

    public static async ntlm()
    {
        const device = await AuthService.getDeviceInfo();

        return (await axios.post<TokenModel>('auth/ntlm', {
            deviceId: device.deviceId,
            deviceName: device.deviceName
        }, {
            withCredentials: true
        })).data;
    }

    public static async validateToken()
    {
        return (await axios.get<TokenModel>('auth/token/validate', {
            withCredentials: true
        })).data;
    }

    public static async refreshToken()
    {
        const device = await AuthService.getDeviceInfo();

        return (await axios.get<TokenModel>('auth/token/refresh', {
            params: {
                id: device.deviceId,
                name: device.deviceName
            },
            withCredentials: true
        })).data;
    }

    public static async recoverToken(refreshToken: string = null)
    {
        const device = await AuthService.getDeviceInfo();

        return (await axios.post<TokenModel>('auth/token/recover', {
            token: refreshToken, // Jeśli jest null, to będzie próbował odczytać z ciasteczka
            deviceId: device.deviceId,
            deviceName: device.deviceName
        }, {
            withCredentials: true
        })).data;
    }

    public static async revokeToken(refreshToken: string)
    {
        return (await axios.post<Statement>('auth/token/revoke', {
            token: refreshToken
        })).data;
    }

    public static async getIdentity(real: boolean = false)
    {
        return (await axios.get<AuthModel>(real ? 'auth/profile/me' : 'auth/profile')).data;
    }

    public static async updateProfile(user: ProfileModel)
    {
        return (await axios.put<Statement>('auth/profile', user)).data;
    }

    public static async updatePreferredLanguage(preferredLanguage: string)
    {
        return (await axios.put<Statement>(`auth/profile/language/${preferredLanguage}`)).data;
    }

    public static async resetPassword(email: string)
    {
        return (await axios.post<string>('auth/password/reset', {
            email: email
        })).data;
    }

    public static async setPassword(token: string, email: string, newPassword: string, repeatPassword: string)
    {
        return (await axios.post<string>('auth/password/set', {
            token: token,
            email: email,
            newPassword: newPassword,
            repeatPassword: repeatPassword
        })).data;
    }

    public static async changePassword(model: PasswordModel)
    {
        return (await axios.post<string>('auth/password/change', model, {
            withCredentials: true
        })).data;
    }

    public static async passwordExpires()
    {
        return (await axios.get<number>('auth/password/expire')).data;
    }

    public static async getSubstitutions()
    {
        return (await axios.get<Substitution[]>('auth/substitutions')).data;
    }

    public static async getDevices(pager: Pager)
    {
        return (await axios.get<Pagination<DeviceModel>>('auth/devices', {
            params: pager.data()
        })).data;
    }

    public static async removeDevice(deviceId: string)
    {
        return (await axios.delete(`auth/devices/${deviceId}`)).data as Statement;
    }

    public static async getSessionSettings()
    {
        return (await axios.get<SessionSettings>('auth/session')).data;
    }

    public static async revokeSession(sessionId: number)
    {
        return (await axios.delete(`auth/session/${sessionId}/revoke`)).data as Statement;
    }
}

export interface AuthTokenModel
{
    tokenModel: TokenModel;
    mfa: MfaModel;
}

export interface TokenModel
{
    token: string;
    refresh: string;
    expires: string;
}

export interface MfaModel
{
    enabled: boolean;
    publicId: string;
    token: string;
}

export interface AuthModel
{
    id: number;
    publicId: string;
    userName: string;
    email: string;
    givenName: string;
    surname: string;
    facePicture: FileResource;
    forcePasswordChange: boolean;
    accountType: AccountType;
    systemVersion: string;
    preferredLanguage: string;
    document: any;
}

export interface ProfileModel
{
    userName: string;
    email: string;
    givenName: string;
    surname: string;
    facePicture: FileResource;
    document: any;
}

export interface LoginModel
{
    username: string;
    password: string;
    rememberMe: boolean;
    forceLogin: boolean;
}

export interface PasswordModel
{
    currentPassword: string;
    newPassword: string;
    repeatPassword: string;
}

export interface ResetModel
{
    email: string;
    newPassword: string;
    repeatPassword: string;
}

export interface Substitution
{
    id: number;
    name: string;
    intervals: Interval[];
}

export interface Interval
{
    dateBeginUtc: DateTime;
    dateEndUtc: DateTime;
}

export interface DeviceInfo
{
    deviceId: string;
    deviceName: string;
}

export interface DeviceModel
{
    id: number;
    dateCreatedUtc: DateTime;
    dateModifiedUtc: DateTime;
    deviceId: string;
    deviceName: string;
    ipAddress: string;
}

export interface SessionSettings
{
    sessionDuration: number;
}
